/**
 * /execution/vetting/:contractorId — single-contractor execution deep-dive.
 *
 * Pure read. Aggregates vetting checklist, executionScore, last 30d
 * callbacks/holdbacks/disputes, and an inline ExecutionTimeline for any
 * job the contractor was the installer on.
 */
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import { computeExecutionScore } from "@/data/contractor/networkExecution/executionScore";
import { VETTING_PROMOTION_REQUIREMENTS } from "@/data/contractor/networkExecution/vettingPromotion";
import { MIN_SAMPLE_FOR_DISPLAY } from "@/lib/analyticsThresholds";

const DAY = 86_400_000;

export default function ExecutionVettingPage() {
  const { contractorId } = useParams<{ contractorId: string }>();
  const { role } = useRole();
  const { contractors, jobs, callbacks, holdbackLedger, disputeEvents } = useAppData();

  const contractor = contractors.find((c) => c.id === contractorId);

  const summary = useMemo(() => {
    if (!contractor) return null;
    const cutoff = Date.now() - 30 * DAY;
    const cb = callbacks.filter((k) => k.installerContractorIdAtCallback === contractor.id);
    const cb30 = cb.filter((k) => new Date(k.filedAt).getTime() >= cutoff);
    const hb = holdbackLedger.filter((h) => h.contractorId === contractor.id);
    const disp = disputeEvents.filter((d) => d.contractorId === contractor.id);
    const score = computeExecutionScore({ contractor, jobs, callbacks }).score;
    const v = contractor.vetting;
    const photos = v?.submittedJobPhotos.length ?? 0;
    const checks = {
      license: v?.licenseVerified === true,
      insurance: v?.insuranceVerified === true,
      photos: photos >= VETTING_PROMOTION_REQUIREMENTS.minPhotos,
      skill: v?.skillChecklistCompleted === true,
      jobs: (contractor.jobsCompleted ?? 0) >= VETTING_PROMOTION_REQUIREMENTS.minJobsCompleted,
      score: score !== null && score >= VETTING_PROMOTION_REQUIREMENTS.minScore,
    };
    return { cb, cb30, hb, disp, score, photos, checks };
  }, [contractor, jobs, callbacks, holdbackLedger, disputeEvents]);

  if (!canAccessExecutionDomain(role)) return null;

  if (!contractor || !summary) {
    return (
      <CockpitPage title="Contractor not found">
        <Card className="p-6 text-sm text-muted-foreground">
          No contractor with id <code>{contractorId}</code>.
        </Card>
      </CockpitPage>
    );
  }

  return (
    <CockpitPage
      
      title={contractor.companyName}
      maxWidth="7xl"
      controls={
        <Button asChild variant="outline" size="sm">
          <Link to={`/contractors/${contractor.id}`}>Open profile</Link>
        </Button>
      }
    >
      {/* Status row */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Badge variant="outline" className="text-[10px]">{contractor.vettingStatus ?? "unknown"}</Badge>
        {contractor.isOnProbation && (
          <Badge className="bg-warning/15 text-warning border-warning/30 text-[10px]">On probation</Badge>
        )}
        <Badge variant="outline" className="text-[10px]">businessRole: {contractor.businessRole ?? "executor"}</Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Execution score" value={summary.score === null ? "—" : String(summary.score)} sub={summary.score === null ? `sample < ${MIN_SAMPLE_FOR_DISPLAY}` : "out of 100"} />
        <Stat label="Jobs completed" value={contractor.jobsCompleted ?? 0} />
        <Stat label="Callbacks 30d" value={summary.cb30.length} tone={summary.cb30.length > 0 ? "warn" : "default"} />
        <Stat label="Total callbacks" value={summary.cb.length} />
      </div>

      <h2 className="text-[12px] font-bold uppercase tracking-[0.16em] text-muted-foreground mb-2">Promotion gate</h2>
      <Card className="p-4 mb-6">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[12px]">
          <Check ok={summary.checks.license}>License verified</Check>
          <Check ok={summary.checks.insurance}>Insurance verified</Check>
          <Check ok={summary.checks.photos}>Photos: {summary.photos} / {VETTING_PROMOTION_REQUIREMENTS.minPhotos}</Check>
          <Check ok={summary.checks.skill}>Skill checklist completed</Check>
          <Check ok={summary.checks.jobs}>Jobs completed: {contractor.jobsCompleted ?? 0} / {VETTING_PROMOTION_REQUIREMENTS.minJobsCompleted}</Check>
          <Check ok={summary.checks.score}>Score ≥ {VETTING_PROMOTION_REQUIREMENTS.minScore}</Check>
        </ul>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="text-[12px] font-bold uppercase tracking-[0.16em] text-muted-foreground mb-2">Recent holdbacks</h3>
          {summary.hb.length === 0
            ? <p className="text-[12px] text-muted-foreground">None.</p>
            : <ul className="text-[11px] space-y-1">{summary.hb.slice(0, 8).map((h) => <li key={h.id} className="flex items-center justify-between"><span className="font-mono">{h.jobId}</span><Badge variant="outline" className="text-[10px]">{h.action}</Badge></li>)}</ul>}
        </Card>
        <Card className="p-4">
          <h3 className="text-[12px] font-bold uppercase tracking-[0.16em] text-muted-foreground mb-2">Recent disputes</h3>
          {summary.disp.length === 0
            ? <p className="text-[12px] text-muted-foreground">None.</p>
            : <ul className="text-[11px] space-y-1">{summary.disp.slice(0, 8).map((d) => <li key={d.id} className="flex items-center justify-between"><span className="font-mono">{d.jobId}</span><Badge variant="outline" className="text-[10px]">{d.type}</Badge></li>)}</ul>}
        </Card>
      </div>
    </CockpitPage>
  );
}

function Stat({ label, value, sub, tone = "default" }: { label: string; value: number | string; sub?: string; tone?: "default" | "warn" }) {
  return (
    <Card className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-0.5 ${tone === "warn" ? "text-warning" : ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

function Check({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      <span className={`inline-flex h-4 w-4 rounded-full text-[10px] items-center justify-center ${ok ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
        {ok ? "✓" : "—"}
      </span>
      <span className={ok ? "text-foreground" : "text-muted-foreground"}>{children}</span>
    </li>
  );
}
